import {getTypedEntries} from "@/core/util/getTypedEntries";
import {randID} from "@/core/util/randID";
import {randomNum} from "@/core/util/randomNum";
import {BasketItem} from "@/gql/gen";
import {ProductModel} from "@/modules/shop/composable/useProduct";
import {
    BasketItemModel,
    ProductModificatorSelectedOption,
    ProductModificatorsSelected,
} from "@/modules/shop/composable/util";
import {useBasketStore} from "@/modules/shop/store/basket";
import {useCatalogStore} from "@/modules/shop/store/catalog";
import {useProductModificatorStore} from "@/modules/shop/store/product-modificator";
import {BasketItemError, BasketItemStatus, fillBasketItemByProduct,} from "@/modules/shop/store/util/basket";
import {ProductDescriptor, ProductModificatorDescriptor,} from "@/modules/shop/store/util/catalog";
import {isChildModificator, ProductModificatorValues} from "@/modules/shop/store/util/product-modificator";
import {useVorderTotalStore} from "@/modules/shop/store/vorder-total";
import {storeToRefs} from "pinia";
import {computed, nextTick, reactive, ref} from "vue";

function createClientId() {
    return randomNum(-10000000, -1000000);
}

export const useBasketItemModel = (
    source: BasketItem | BasketItemModel,
    product: ProductModel
): BasketItemModel => {
    if ("_isModel" in source) {
        return source;
    }

    const basketStore = useBasketStore();
    const totalStore = useVorderTotalStore();
    const productModificatorStore = useProductModificatorStore();

    const {getProductModificator} = productModificatorStore
    const {basketItems, basketModificatorsStat} = storeToRefs(basketStore);

    const fields = reactive<BasketItem>({...source});

    const state = reactive({
        clientChangedAt: Date.now(),
        needSync: false,
        isDeleted: false,
    });

    const commited = ref(false);

    if (!fields.ID) fields.ID = createClientId();
    if (!fields.UUID) fields.UUID = randID();

    fields.CLIENT_CHANGED_AT = state.clientChangedAt;

    const children = computed<BasketItemModel[]>(() => {
        let items = basketItems.value.filter((item: BasketItemModel) => getUuid() === item.getParentUuid())

        items.sort(function (a, b) {
            const modA = a.getReasonModificator()
            const priorA = modA?.TYPE === 'gift'
            if (priorA)
                return -1;
            return a - b;
        })

        return items
    });

    const isRoot = computed(() => getIsRoot());

    const errors = computed(() => {
        const result: BasketItemError[] = []
        if (product) {
            /*
            if (deliveryStore.deliveryDate) {
                if (!product.checkSchedule(deliveryStore.deliveryDate.toDate())) {
                    const scheduleTitles = product.getSchedule()?.titles || []
                    if (scheduleTitles.length) {
                        result.push({
                            icon: true,
                            type: 'schedule',
                            message: 'Доступен только в: ' + scheduleTitles.join(', ')
                        })
                    }
                }
            }
             */
        }
        return result
    })

    /**
     * Статус элемента корзины (лимиты по модификаторам)
     *
     * Для связанных товаров (REASON_MOD_ID) возвращает информацию о лимитах
     * Для обычных товаров возвращает Infinity (нет ограничений)
     */
    const status = computed<BasketItemStatus>(() => {
        const modId = fields.PROPERTIES.REASON_MOD_ID;
        const productId = fields.PRODUCT_ID;

        if (!modId) {
            // Обычный товар - нет ограничений
            return {
                quantity: fields.QUANTITY,
                quantityLimit: Infinity,
                quantityAvailable: Infinity,
                haveError: false,
            };
        }

        // Связанный товар - ищем по ключу modId_productId
        const key = `${modId}_${productId}`;
        const stat = basketModificatorsStat.value[key];

        if (!stat) {
            // Статистика еще не рассчитана или модификатор не найден
            return {
                quantity: fields.QUANTITY,
                quantityLimit: 0, // Нет лимита = нельзя добавлять
                quantityAvailable: 0,
                haveError: true,
            };
        }

        return stat;
    });

    function setCommitted() {
        commited.value = true;
    }

    function markChanged() {
        state.clientChangedAt = Date.now();
        fields.CLIENT_CHANGED_AT = state.clientChangedAt;
        state.needSync = true;
    }

    async function remove() {
        fields.QUANTITY = 0;

        // Если товар дочерний то удаляем у родительского fields.MODIFICATORS
        if (getIsChild()) {
            const reasonModId = fields.PROPERTIES.REASON_MOD_ID;
            if (!reasonModId)
                return;

            const productModificatorStore = useProductModificatorStore();
            const modificator = productModificatorStore.productModificatorsById[reasonModId];

            if (modificator) {
                // Найти все основные товары с этим модификатором
                const hostBasketItems = basketStore.basketItems.filter((basketItem) => {
                    if (basketItem.fields.PROPERTIES.REASON_MOD_ID) return false;
                    if (basketItem.fields.PARENT_ID) return false;

                    const modificators =
                        productModificatorStore.productModificatorsByProductId[
                            basketItem.product.ID
                            ] || [];
                    return modificators.some((mod) => mod.ID === reasonModId);
                });

                // Найти опцию модификатора для этого товара
                const option = modificator.OPTIONS.find(
                    (opt) => opt.PRODUCT_ID === fields.PRODUCT_ID
                );

                if (option) {
                    // Сбросить значение модификатора для этой опции во всех основных товарах
                    for (const hostBasketItem of hostBasketItems) {
                        const modValue = hostBasketItem.fields.MODIFICATORS[modificator.GROUP_CODE];
                        if (modValue && typeof modValue === 'object') {
                            const newModValue = {...(modValue as any)};
                            delete newModValue[option.UID];

                            // Если объект стал пустым - удаляем весь ключ
                            if (Object.keys(newModValue).length === 0) {
                                delete hostBasketItem.fields.MODIFICATORS[modificator.GROUP_CODE];
                            } else {
                                hostBasketItem.fields.MODIFICATORS[modificator.GROUP_CODE] = newModValue;
                            }

                            hostBasketItem.markChanged();
                        }
                    }
                }
            }
        }

        await nextTick();
        basketStore.basketRemoveItem(fields.ID);
        markChanged();

        // Проверяем превышения лимитов ПОСЛЕ удаления товара
        // Глобальные модификаторы проверяются автоматически через watch в basketStore
        await nextTick();
        basketStore.basketRemoveExcessItems();
    }

    function getUuid() {
        return fields.UUID;
    }

    function setParent(itemModel: BasketItemModel) {
        fields.PARENT_UUID = itemModel.getUuid()
    }

    function getParentUuid() {
        return fields.PARENT_UUID
    }

    function getParentItem() {
        return getUuid() ? basketStore.basketItems.find(item => item.getUuid() === getUuid()) : null;
    }

    function getIsRoot() {
        return !getParentUuid()
    }


    /**
     * Изменить количество товара
     *
     * При изменении количества основного товара запускается общая валидация корзины,
     * которая автоматически пересчитает лимиты для всех связанных товаров
     *
     * Для товаров с модификаторами проверяет лимит и показывает уведомление при превышении
     */
    async function updateQuantity(quantity: number, options: { silent?: boolean } = {}) {
        fields.QUANTITY = quantity;
        if (!options.silent) {
            basketStore.basketRemoveExcessItems();
        }
        await syncChildren()
        markChanged()
    }

    async function setQuantity(quantity: number, options: { silent?: boolean } = {}) {
        if (quantity > 0) {
            await updateQuantity(quantity, options)
        } else {
            await remove()
        }
    }

    /**
     * Добавить дочерний товар, связанный через модификатор
     */
    async function addChildItemByModificator(
        modificator: ProductModificatorDescriptor,
        product: ProductDescriptor,
        quantity: number,
    ) {
        const catalogStore = useCatalogStore();

        const productModel = await catalogStore.ensureProduct(product);
        if (!productModel) return;

        const mod = getProductModificator(modificator, productModel.ID);
        if (!mod) return;

        let existingItem = basketItems.value.find((item: BasketItemModel) => item.fields.PRODUCT_ID === productModel.ID)

        if (!existingItem) {
            const item = fillBasketItemByProduct(
                {
                    QUANTITY: quantity,
                    PARENT_UUID: getUuid()
                },
                productModel,
                mod
            );
            await basketStore.basketAddItem(item, productModel);
        }
        markChanged();
    }

    async function setGift(giftProductId: ProductDescriptor) {
    }

    function getPrice() {
        const mod = getReasonModificator()
        if (mod) {
            if (mod.TYPE === 'gift') {
                return 0
            }
        }
        return fields.PRICE_BASE || 0;
    }

    function getPriceTotal() {
        return getPrice() * fields.QUANTITY;
    }

    function updateFromServer(data: any) {
    }

    function getFakeSync() {
    }

    function isIngredientRemoved(elementId: number) {
        return fields.PROPERTIES.INGREDIENTS_REMOVED.includes(elementId);
    }

    function setIngredientState(elementId: number, state?: boolean) {
        if (typeof state === "undefined") {
            if (isIngredientRemoved(elementId)) {
                fields.PROPERTIES.INGREDIENTS_REMOVED =
                    fields.PROPERTIES.INGREDIENTS_REMOVED.filter(
                        (id) => elementId !== id
                    );
            } else {
                fields.PROPERTIES.INGREDIENTS_REMOVED.push(elementId);
            }
        }
    }

    function getReasonModificatorId() {
        return fields.PROPERTIES.REASON_MOD_ID
    }

    function getReasonModificatorCode() {
        return fields.PROPERTIES.REASON_MOD_CODE
    }

    function getReasonModificator() {
        const modId = getReasonModificatorId()
        return modId ? getProductModificator(modId) : null
    }

    function getReasonRuleId() {
        return fields.PROPERTIES.REASON_RULE_ID
    }

    function getReasonRuleCode() {
        return fields.PROPERTIES.REASON_RULE_CODE
    }


    /**
     * Получить значение модификатора из корзины
     *
     * Значения хранятся в fields.MODIFICATORS[GROUP_CODE]
     * Формат значения: { [UID]: quantity } (например: { "123": 2, "124": 1 })
     *
     * Если значение не найдено, возвращается DEFAULT_VALUE модификатора
     *
     * @param modificator Дескриптор модификатора
     * @returns Значение модификатора { [uid]: quantity } или {}
     */
    function getModificatorValue(modificator: ProductModificatorDescriptor) {
        let value = null;
        const mod = getProductModificator(modificator, fields.PRODUCT_ID);
        if (mod) {
            // Значения хранятся по GROUP_CODE, а не по ID модификатора!
            value = fields.MODIFICATORS[mod.GROUP_CODE];
            if (!value) value = mod.DEFAULT_VALUE; // Fallback на значение по умолчанию
        }
        value = value || {};
        return value;
    }

    /**
     * Установить значение модификатора в корзине
     *
     * Значение сохраняется в fields.MODIFICATORS[GROUP_CODE]
     * Формат значения: { [UID]: quantity }
     *
     * После установки значения автоматически синхронизируются связанные товары:
     * - Удаляются товары, которых больше нет в выборе
     * - Добавляются/обновляются товары по новому выбору
     *
     * ВАЖНО: Синхронизация происходит только если товар уже добавлен в корзину (commited=true)
     * Это предотвращает автоматическое добавление связанных товаров при выборе модификаторов
     * на карточке товара до его добавления в корзину.
     *
     * @param modificator Дескриптор модификатора
     * @param value Значение в формате { [uid]: quantity }
     */
    async function setModificatorValue(
        modificator: ProductModificatorDescriptor,
        value: any
    ) {
        const mod = getProductModificator(modificator, fields.PRODUCT_ID);

        if (mod) {
            fields.MODIFICATORS[mod.GROUP_CODE] = value;
            // Автоматическая синхронизация связанных товаров
            // ТОЛЬКО если товар уже в корзине (не в процессе настройки на карточке)
            if (commited.value) {
                await syncChildren();
            }

            markChanged();
        }
    }

    /**
     * Computed: выбранные модификаторы с полной информацией
     *
     * Преобразует сырые значения из fields.MODIFICATORS в структурированные данные:
     * - mod: полный объект модификатора
     * - value: сырое значение { [uid]: quantity }
     * - valueOptions: обогащенные опции с полной информацией (option + valueQuantity)
     *
     * Используется для отображения выбранных модификаторов в UI
     */
    const selectedModificators = computed(() => {
        const result: ProductModificatorsSelected = {};
        for (const [modCode, modValue] of Object.entries(
            fields.MODIFICATORS as ProductModificatorValues
        )) {
            const mod = getProductModificator(modCode, fields.PRODUCT_ID);
            if (!mod) continue;
            const valueOptions = Object.entries(modValue as object).reduce<
                Record<string, ProductModificatorSelectedOption>
            >((map, item) => {
                const [uid, quantity] = item;
                const option = mod.OPTIONS.find((option) => option.UID === uid);
                if (option) {
                    map[uid] = {
                        option,
                        valueQuantity: quantity,
                    };
                }
                return map;
            }, {});
            result[modCode] = {
                mod,
                value: modValue,
                valueOptions,
            };
        }
        return result;
    });

    /**
     * Синхронизировать дочерние товары с выбором модификаторов
     */
    async function syncChildren() {

        const usedProductIds: any = {}

        for (const [modCode, modValue] of Object.entries(fields.MODIFICATORS as ProductModificatorValues)) {
            const mod = getProductModificator(modCode, fields.PRODUCT_ID);
            if (!mod) continue;
            if (isChildModificator(mod)) {
                for (const [valueUid, valueQuantity] of Object.entries(modValue)) {
                    const totalQuantity = fields.QUANTITY * valueQuantity
                    const modOption = mod.OPTIONS.find((option) => option.UID === valueUid);
                    if (!modOption)
                        continue;
                    const productId = modOption.PRODUCT_ID
                    if (productId) {
                        usedProductIds[productId] = productId
                        const existingChildItem = children.value.find(item => item.fields.PRODUCT_ID === productId)
                        if (!existingChildItem) {
                            await addChildItemByModificator(mod, productId, totalQuantity)
                        } else {
                            if (existingChildItem.fields.QUANTITY !== totalQuantity) {
                                existingChildItem.fields.QUANTITY = totalQuantity
                                existingChildItem.markChanged()
                            }
                        }
                    }
                }
            }
        }

        const itemsToDelete: BasketItemModel[] = []

        for (const childItem of children.value) {
            if (!usedProductIds[childItem.fields.PRODUCT_ID]) {
                itemsToDelete.push(childItem);
            }
        }

        if (itemsToDelete.length) {
            basketStore.basketRemoveItems(itemsToDelete)
        }
    }

    const calc = computed(() => {
        return totalStore.basketItemsCalculatedById[fields.ID]?.calc!;
    })



    const getIsAddedByModificator = () => {
        return !!fields.PROPERTIES.REASON_MOD_ID || !!fields.PARENT_UUID
    }

    const getIsAddedByRule = () => {
        return !!fields.PROPERTIES.REASON_RULE_ID
    }

    const getIsChild = () => {
        return !!fields.PARENT_UUID
    }

    const isEqualWithItem = (compareItem: BasketItemModel) => {

        if (getUuid() === compareItem.getUuid())
            return true;

        if (fields.PRODUCT_ID !== compareItem.fields.PRODUCT_ID) {
            return false
        }

        for (const [modCode, modValue] of Object.entries(fields.MODIFICATORS as ProductModificatorValues)) {
            const compareModValue = compareItem.fields.MODIFICATORS[modCode]
            if (modValue && !compareModValue || !modValue && compareModValue)
                return false;
            if (modValue) {
                for (const [modValueUid, modValueQuantity] of getTypedEntries(modValue)) {
                    if (compareModValue[modValueUid] !== modValueQuantity) {
                        return false;
                    }
                }
            }
        }

        return true
    }

    const isGift = computed(() => {
        const mod = getReasonModificator()
        return mod ? mod.TYPE === 'gift' : false
    })

    const getApiData = () => {
        return {
            UUID: getUuid(),
            NAME: fields.NAME,
            PRODUCT_ID: fields.PRODUCT_ID,
            QUANTITY: fields.QUANTITY,
            PARENT_UUID: getParentUuid(),
            IS_GIFT: isGift.value
        }
    }

    return {
        _isModel: true,
        id: fields.ID,
        parentId: fields.PRODUCT_ID,
        fields,
        product,
        state,
        isRoot,
        children,
        status,
        errors,
        getUuid,
        setParent,
        setQuantity,
        addQuantity: updateQuantity,
        remove,
        setGift,
        getPrice,
        getPriceTotal,
        markChanged,
        updateFromServer,
        getFakeSync,
        commited,
        setCommitted,
        setIngredientState,
        setModificatorValue,
        getModificatorValue,
        syncChildren: syncChildren,
        getApiData,
        calc,
        selectedModificators,
        getIsAddedByModificator,
        getIsChild,
        getIsRoot,
        getParentUuid,
        getParentItem,
        isEqualWithItem,
        getReasonModificatorId,
        getReasonModificatorCode,
        getReasonModificator,
        getReasonRuleId,
        getReasonRuleCode,
        getIsAddedByRule,
        isGift
    };
};
